import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Pagination } from "../../../../common/pagination";
import ModernPagination from "../../../../common/pagination/ModernPagination";
import Heading from '../../../../common/element/Heading';
import ClientInput from "../../../../common/element/Input/clientInput";
import BtnPrimary from "../../../../common/element/Buttons/btnPrimary";
import { debounce } from "../../../../common/snippet/Debouncing";
import { setBreadcrumbs } from "../../../../redux/Admin/breadcrumbsSlice";
import { useDispatch, useSelector } from 'react-redux';
import SessionList from './SessionMaster'; // Note: Ensure this imports the SessionList component correctly
import AddEditSession from './AddEdit';
import { getSessionMasterList } from '../../../../redux/SessionMaster/sessionMasterSlice';
import { selectSessionMasterList, selectSessionMasterListLoading, selectSessionMasterListTotal } from '../../../../redux/SessionMaster/sessionMasterSelector';
import StatusSelect from '../../../../common/element/StatusSelect/StatusSelect';

const SessionMaster = () => {
  const totalRecords = useSelector(selectSessionMasterListTotal);
  const [status, setStatus] = useState('false');
  
  // NEW: State for the Tabs. Defaulting to 'Employee' based on your image
  const [activeTab, setActiveTab] = useState('2');

  const [countData, setCountData] = useState(totalRecords);
  const [filterpage, setfilterpage] = useState(1);
  const [filterlimit, setfilterlimit] = useState(10);
  const [totalPages, setTotalPages] = useState(1);
  const dispatch = useDispatch();
  const [dialogOpen, setDialogOpen] = useState(false);
  const selectSessionMasterListData = useSelector(selectSessionMasterList);
  const loading = useSelector(selectSessionMasterListLoading);
  
  const [getFilterData, SetFilterData] = useState({
    function_assignment_id: "",
    page: 1,
    limit: 10,
    sort: "created_date",
    order: "desc",
    search: "",
  });

  // NEW: Tab Options Configuration
  const tabOptions = [
    { label: "Employee", value: "2" },
    { label: "Management", value: "1" },
    { label: "Prospect Company", value: "5" },
    { label: "Science of Self", value: "4" },
    { label: "Management & Employee", value: "3" },
    { label: "Sales Training Companies", value: "6" },
  ];

  // Search Data
  const handleSearch = (e) => {
    SetFilterData((prevalue) => {
      return {
        ...prevalue,
        [e.target.name]: e.target.value.trim(),
      };
    });
  };
  
  useEffect(() => {
    setCountData(totalRecords || 0);
    if (totalRecords && filterlimit) {
      setTotalPages(Math.ceil(totalRecords / filterlimit));
    }
  }, [totalRecords, filterlimit]);
  
  useEffect(() => {
    dispatch(
      setBreadcrumbs([
        {
          label: "Session Master",
          url: "/session-master",
          home: "/companies",
        },
      ])
    );
  }, [dispatch]); // Added dependency

  const inputchangehandler = useCallback(debounce(handleSearch), []);

  // Function to refresh the session list
  const refreshSessionList = useCallback(() => {
    let payload = {   
      page: filterpage, 
      limit: filterlimit, 
      search: getFilterData.search,
      archived: status,
      access_group_id: activeTab // NEW: Sending the active tab value to API
    };
    
    console.log(payload, "Payload sent to API");

    dispatch(getSessionMasterList(payload));
  }, [dispatch, filterpage, filterlimit, getFilterData.search, status, activeTab]); // Added activeTab to dependency array

  useEffect(() => {
    refreshSessionList();
  }, [refreshSessionList]);

  const handlePageChange = (page) => {
    setfilterpage(page);
  };

  // NEW: Handle Tab Change
  const handleTabChange = (tabValue) => {
    setActiveTab(tabValue);
    setfilterpage(1); // Reset to page 1 when switching tabs
  };

  const filteredData = useMemo(() => {
    if (!selectSessionMasterListData || selectSessionMasterListData.length === 0) return [];
    
    // Note: If the API handles filtering by 'activeTab', we don't strictly need to filter here again,
    // but we keep the search filter logic for client-side responsiveness if needed.
    if (!getFilterData.search) return selectSessionMasterListData;
    
    const searchValue = getFilterData.search.toLowerCase();

    return selectSessionMasterListData.filter(
      (item) =>
        item.session_name?.toLowerCase().includes(searchValue) ||
        item.tool_name?.toLowerCase().includes(searchValue) ||
        item.created_by?.name?.toLowerCase().includes(searchValue)
    );
  }, [getFilterData.search, selectSessionMasterListData]);

 
  const handleStatusChange = (val) => {
    setStatus(val);
    setfilterpage(1); 
  };

  return (
    <React.Fragment>
      <div>
        <p className="text-gray-600 my-3"></p>
      </div>

      <div className="flex justify-between mb-5">
        <div className="w-80 h-10 text-start">
          <Heading>Session Master</Heading>
        </div>

        <div className="flex gap-2">
          <BtnPrimary
            onClick={() => setDialogOpen(true)}
            className="text-xs h-9 flex items-center justify-center px-3 gap-1"
          >
            Add Session 
            <svg
              width="21"
              height="20"
              viewBox="0 0 21 20"
              fill="none"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                d="M10.712 4.167v11.666M4.878 10h11.667"
                stroke="#fff"
                strokeWidth="1.667"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          </BtnPrimary>
        </div>
      </div>

      {/* NEW: Tab Navigation Section */}
      <div className="w-full bg-[#FFF4E5] p-1.5 rounded-lg flex items-center justify-between mb-5">
        {tabOptions.map((tab) => (
          <button
            key={tab.value}
            onClick={() => handleTabChange(tab.value)}
            className={`flex-1 text-sm font-medium py-1.5 rounded-md transition-all duration-200 ${
              activeTab === tab.value
                ? "bg-white text-gray-900 shadow-sm" // Active State
                : "text-gray-500 hover:text-gray-700 bg-transparent" // Inactive State
            }`}
          >
            {tab.label}
          </button>
        ))}
      </div>
      {/* End Tab Navigation */}

      <div className="flex justify-end mb-4">
        <div className='me-3'>
          <StatusSelect
            value={status}
            onChange={handleStatusChange}
            placeholder="Select Status"
          />
        </div>
        <div className="input-wrapper">
          <div className="input-suffix-start left-3.5">
            <img
              src="/assets/icons-svg/search-lg.svg"
              alt=""
              className="h-5"
            />
          </div>
          <ClientInput
            type="text"
            className="input pl-10 w-80 input-shadow h-10 text-sm border-gray-300 rounded-lg appearance-none border text-gray-700 leading-tight focus:outline-none focus:shadow-outline"
            placeholder="Search"
            inputchangehandler={inputchangehandler}
            name={"search"}
          />
        </div>
      </div>

      <SessionList 
        sampleData={filteredData} 
        onRefresh={refreshSessionList}
        currentPage={filterpage}        
        pageSize={filterlimit}
        loading={loading}
      />
     
      <AddEditSession
        dialogOpen={dialogOpen}
        onDialogClose={() => {
          setDialogOpen(false);
          refreshSessionList();
        }}
        onSuccess={refreshSessionList}
      />

      <div className="flex justify-between items-center mt-3">
        {/* Left side - Pagination info */}
        <Pagination
          totalCount={countData}
          pageNumber={filterpage}
          displayPage={filteredData?.length}
          UpdatePageLimit={setfilterlimit}
          UpdatePage={setfilterpage}
        />

        {/* Right side - Modern Pagination Controls */}
        {countData > 0 && totalPages > 1 && (
          <ModernPagination
            totalPages={totalPages}
            currentPage={filterpage}
            onPageChange={handlePageChange}
          />
        )}
      </div>
    </React.Fragment>
  )
}

export default SessionMaster;